import React, {useId, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Button, Flex, SimpleGrid, Text} from '@chakra-ui/react'
import Link from '../../../components/link'
import {HOME_HREF} from '../../../constants'
import {useWarrantyContext, WARRANTY_MODES} from '../util/warranty-context'
import {FormattedDate, FormattedMessage, useIntl} from 'react-intl'
import {parseBackendDate} from '../../../utils/date-utils'

const FieldDisplay = ({children, label, ...props}) => (
    <Flex direction="column" gap={1} {...props}>
        {label ? (
            <Text textStyle="Regular Small" color="stokkeCore.accessibleGrey">
                {label}
            </Text>
        ) : null}
        <Text>{children}</Text>
    </Flex>
)

FieldDisplay.propTypes = {
    children: PropTypes.node.isRequired,
    label: PropTypes.string
}

const WarrantyConfirmation = () => {
    const warrantyHeaderId = useId()
    const {formatMessage} = useIntl()
    const {data, mode} = useWarrantyContext()
    const parsedPurchaseDate = useMemo(
        () => parseBackendDate(data.purchaseDate),
        [data.purchaseDate]
    )
    const parsedManufactureDate = useMemo(
        () => parseBackendDate(data.manufactureDate),
        [data.manufactureDate]
    )

    return (
        <Flex direction="column" gap={8} marginInline={-4}>
            <Flex
                direction="column"
                alignItems="center"
                gap={6}
                padding={10}
                color="stokkeCore.white"
                backgroundColor="stokkeFunctional.alertGreen"
            >
                <Text textAlign="center">
                    <FormattedMessage
                        defaultMessage="Thank you"
                        id="warranty.confirmation.thank_you"
                    />
                </Text>

                <Text as="h1" textAlign="center" textStyle="Regular 2XLarge">
                    {mode === WARRANTY_MODES.CPSIA ? (
                        <FormattedMessage
                            defaultMessage="Your CPSIA registration is sucessful"
                            id="warranty.confirmation.cpsia_heading"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Your product is now registered"
                            id="warranty.confirmation.heading"
                        />
                    )}
                </Text>

                {mode === WARRANTY_MODES.WARRANTY && (
                    <Text textAlign="center">
                        <FormattedMessage
                            defaultMessage="A product certificate will be sent to your email address as soon as we have verified the information provided"
                            id="warranty.confirmation.product_certificate"
                        />
                    </Text>
                )}

                <Button as={Link} to={HOME_HREF}>
                    <FormattedMessage
                        defaultMessage="Continue Shopping"
                        id="global.continue_shopping"
                    />
                </Button>
            </Flex>

            <SimpleGrid
                as="section"
                columns={{base: 1, md: 2}}
                gap={6}
                padding={10}
                aria-labelledby={warrantyHeaderId}
            >
                <Text as="h2" id={warrantyHeaderId} textStyle="Regular XLarge" gridColumn="1 / -1">
                    {mode === WARRANTY_MODES.CPSIA ? (
                        <FormattedMessage
                            defaultMessage="Summary of your CPSIA registration"
                            id="warranty.confirmation.cpsia_summary"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Your warranty summary"
                            id="warranty.confirmation.warranty_summary"
                        />
                    )}
                </Text>

                <FieldDisplay gridColumn="1 / -1">
                    {data.userInputSelectedProductGroup}
                </FieldDisplay>

                <FieldDisplay
                    label={formatMessage({
                        defaultMessage: 'Product',
                        id: 'warranty.confirmation.product'
                    })}
                >
                    {data.userInputSelectedProductName}
                </FieldDisplay>

                <FieldDisplay
                    label={formatMessage({
                        defaultMessage: 'Serial number',
                        id: 'warranty.confirmation.serial_number'
                    })}
                >
                    {data.serialNumber}
                </FieldDisplay>

                <Text gridColumn="1 / -1">
                    <FormattedMessage
                        defaultMessage="Purchase details"
                        id="warranty.confirmation.purchase_details"
                    />
                </Text>

                <>
                    {data.pointOfPurchase === 'store' ? (
                        <>
                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Where',
                                    id: 'warranty.confirmation.where'
                                })}
                            >
                                <FormattedMessage
                                    defaultMessage="Store"
                                    id="warranty.confirmation.store"
                                />
                            </FieldDisplay>

                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Retailer name',
                                    id: 'warranty.confirmation.retailer_name'
                                })}
                            >
                                {data.userInputRetailerName}
                            </FieldDisplay>

                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Retailer street',
                                    id: 'warranty.confirmation.retailer_address'
                                })}
                            >
                                {data.userInputRetailerStreet}
                            </FieldDisplay>

                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Retailer city',
                                    id: 'warranty.confirmation.retailer_city'
                                })}
                            >
                                {data.userInputRetailerCity}
                            </FieldDisplay>

                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Retailer country',
                                    id: 'warranty.confirmation.retailer_country'
                                })}
                            >
                                {data.userInputRetailerCountry}
                            </FieldDisplay>
                        </>
                    ) : data.pointOfPurchase === 'online' ? (
                        <>
                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Where',
                                    id: 'warranty.confirmation.where'
                                })}
                            >
                                <FormattedMessage
                                    defaultMessage="Online"
                                    id="warranty.confirmation.online"
                                />
                            </FieldDisplay>

                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Name',
                                    id: 'warranty.confirmation.site_name'
                                })}
                            >
                                {data.userInputStoreUrl}
                            </FieldDisplay>
                        </>
                    ) : (
                        <>
                            <FieldDisplay
                                label={formatMessage({
                                    defaultMessage: 'Where',
                                    id: 'warranty.confirmation.where'
                                })}
                            >
                                <FormattedMessage
                                    defaultMessage="Gift"
                                    id="warranty.confirmation.gift"
                                />
                            </FieldDisplay>
                        </>
                    )}
                </>

                <FieldDisplay
                    label={formatMessage({
                        defaultMessage: 'Purchase date',
                        id: 'warranty.confirmation.purchase_date'
                    })}
                >
                    <FormattedDate value={parsedPurchaseDate} />
                </FieldDisplay>

                {mode === WARRANTY_MODES.CPSIA && (
                    <>
                        <FieldDisplay
                            label={formatMessage({
                                defaultMessage: 'Item number',
                                id: 'warranty.label.item_number'
                            })}
                        >
                            {data.userInputItemNumber}
                        </FieldDisplay>

                        <FieldDisplay
                            label={formatMessage({
                                defaultMessage: 'Manufacture date',
                                id: 'warranty.label.manufacture_date'
                            })}
                        >
                            <FormattedDate value={parsedManufactureDate} />
                        </FieldDisplay>
                    </>
                )}
            </SimpleGrid>

            <Flex direction="column" gap={6} paddingInline={10}>
                <Button as={Link} to={HOME_HREF}>
                    <FormattedMessage
                        defaultMessage="Continue Shopping"
                        id="global.continue_shopping"
                    />
                </Button>
            </Flex>
        </Flex>
    )
}

WarrantyConfirmation.propTypes = {
    responseData: PropTypes.object.isRequired
}

export default WarrantyConfirmation
